// @/components/layout/composition-panel.tsx

import React, { useEffect, useRef, useMemo, useCallback, useState } from "react";
import { useDiagram } from "@/context/diagram-context";
import DiagramComponentCard from "@/components/elements/diagram-component-card";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import { cn } from "@/lib/utils";
import type { DiagramComponent } from "@/types/diagram";
import { GripVertical, Info, Move } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Drag and drop context for managing state across components
interface DragState {
  draggedId: string | null;
  dragOverId: string | null;
  dropPosition: "top" | "on" | null;
}

// Pending drop state for position selection
interface PendingDrop {
  draggedId: string;
  targetId: string;
  targetName: string;
  draggedName: string;
  availablePositions: string[];
}

const CompositionTab: React.FC = () => {
  const diagram = useDiagram();
  const componentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollIntervalRef = useRef<number | null>(null);
  const [dragState, setDragState] = useState<DragState>({
    draggedId: null,
    dragOverId: null,
    dropPosition: null,
  });
  const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null);

  useEffect(() => {
    if (diagram.selectedShape && componentRefs.current[diagram.selectedShape]) {
      componentRefs.current[diagram.selectedShape]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [diagram.selectedShape]);

  // Auto-scroll when dragging near edges
  const startAutoScroll = useCallback((direction: "up" | "down") => {
    if (scrollIntervalRef.current) return;

    const scrollSpeed = 8;
    scrollIntervalRef.current = window.setInterval(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop += direction === "down" ? scrollSpeed : -scrollSpeed;
      }
    }, 16);
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  // Handle drag over the scroll container for edge detection
  const handleContainerDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); // Required to allow drop and prevent default browser behavior

    if (!dragState.draggedId || !scrollContainerRef.current) return;

    const rect = scrollContainerRef.current.getBoundingClientRect();
    const edgeThreshold = 60; // pixels from edge to trigger scroll
    const mouseY = e.clientY;

    if (mouseY - rect.top < edgeThreshold) {
      startAutoScroll("up");
    } else if (rect.bottom - mouseY < edgeThreshold) {
      startAutoScroll("down");
    } else {
      stopAutoScroll();
    }
  }, [dragState.draggedId, startAutoScroll, stopAutoScroll]);

  // Clean up auto-scroll on drag end
  useEffect(() => {
    if (!dragState.draggedId) {
      stopAutoScroll();
    }
  }, [dragState.draggedId, stopAutoScroll]);

  // Drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, componentId: string) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", componentId);
    setDragState((prev) => ({ ...prev, draggedId: componentId }));
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragState({ draggedId: null, dragOverId: null, dropPosition: null });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, componentId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    // Calculate if we're dropping "on" or "above" based on mouse position
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const threshold = rect.height * 0.3; // Top 30% = drop above, rest = drop on

    const dropPosition = y < threshold ? "top" : "on";

    setDragState((prev) => ({
      ...prev,
      dragOverId: componentId,
      dropPosition,
    }));
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragState((prev) => ({ ...prev, dragOverId: null, dropPosition: null }));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetId: string, isFirst: boolean) => {
      e.preventDefault();
      const draggedId = e.dataTransfer.getData("text/plain");

      if (!draggedId || draggedId === targetId) {
        handleDragEnd();
        return;
      }

      // Check if trying to drop above first component - not allowed
      if (isFirst && dragState.dropPosition === "top") {
        toast.error("Cannot drop above the first component");
        handleDragEnd();
        return;
      }

      // Get the dragged and target components
      const draggedComponent = diagram.diagramComponents.find((c) => c.id === draggedId);
      const targetComponent = diagram.diagramComponents.find((c) => c.id === targetId);
      if (!draggedComponent || !targetComponent) {
        toast.error("Component not found");
        handleDragEnd();
        return;
      }

      // Get available positions for the target
      const moveTargets = diagram.getAvailableMoveTargets(draggedId);
      const target = moveTargets.find((t) => t.targetId === targetId);

      if (!target || target.positions.length === 0) {
        toast.error(`Cannot move ${draggedComponent.shape} to ${targetComponent.shape} - no available positions`);
        handleDragEnd();
        return;
      }

      // Show position selection dialog
      setPendingDrop({
        draggedId,
        targetId,
        targetName: targetComponent.shape,
        draggedName: draggedComponent.shape,
        availablePositions: target.positions,
      });
      handleDragEnd();
    },
    [diagram, dragState.dropPosition, handleDragEnd]
  );

  // Handle position selection from dialog
  const handlePositionSelect = useCallback(
    (position: string) => {
      if (pendingDrop) {
        try {
          diagram.move3DShape(pendingDrop.draggedId, pendingDrop.targetId, position);
          toast.success(`Moved ${pendingDrop.draggedName} to ${pendingDrop.targetName} at ${position}`);
        } catch (error) {
          toast.error("Failed to move component");
        }
        setPendingDrop(null);
      }
    },
    [diagram, pendingDrop]
  );

  const handleCancelDrop = useCallback(() => {
    setPendingDrop(null);
  }, []);

  const handleScrollToParent = useCallback(
    (parentId: string) => {
      if (componentRefs.current[parentId]) {
        componentRefs.current[parentId]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
        diagram.selectShape(parentId);
      }
    },
    [diagram]
  );

  const componentIndexMap = useMemo(() => {
    const indexMap: { [key: string]: number } = {};
    diagram.diagramComponents.forEach((component, index) => {
      indexMap[component.id] = index;
    });
    return indexMap;
  }, [diagram.diagramComponents]);

  const getParentIndex = useCallback(
    (component: DiagramComponent): number | null => {
      return component.relativeToId !== null
        ? componentIndexMap[component.relativeToId]
        : null;
    },
    [componentIndexMap]
  );

  const nonCutComponents = useMemo(
    () => diagram.diagramComponents.filter((component) => !component.cut),
    [diagram.diagramComponents]
  );

  const cutComponents = useMemo(
    () => diagram.diagramComponents.filter((component) => component.cut),
    [diagram.diagramComponents]
  );

  // Check if a component can be a valid drop target for the dragged item
  const isValidDropTarget = useCallback(
    (targetId: string): boolean => {
      if (!dragState.draggedId) return false;
      const moveTargets = diagram.getAvailableMoveTargets(dragState.draggedId);
      return moveTargets.some((t) => t.targetId === targetId);
    },
    [diagram, dragState.draggedId]
  );

  return (
    <div
      className="flex flex-col flex-1 min-h-0 bg-background text-white"
      role="region"
      aria-label="Diagram composition panel"
    >
      {/* Drag and drop hint - always rendered to prevent layout shift */}
      <div
        className={cn(
          "flex items-center gap-2 px-4 py-2 border-b border-surface-border text-xs text-muted transition-opacity",
          nonCutComponents.length > 1 ? "bg-surface-hover/50 opacity-100" : "h-0 py-0 opacity-0 overflow-hidden border-0"
        )}
        role="note"
        aria-live="polite"
      >
        <Info className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
        <span>Drag components to reposition them. Use keyboard: Tab to navigate, Enter to select, arrow keys to reorder.</span>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {/* Main content */}
        <div
          ref={scrollContainerRef}
          onDragOver={handleContainerDragOver}
          onDragLeave={stopAutoScroll}
          className={cn("flex-1 min-h-0 overflow-auto p-4", CUSTOM_SCROLLBAR)}
        >
          {nonCutComponents.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted" role="status">
              <div className="text-center">
                <p className="text-lg mb-2">No components yet</p>
                <p className="text-sm">
                  Add shapes from the Shapes panel to get started
                </p>
              </div>
            </div>
          ) : (
            <div
              className="space-y-2"
              role="list"
              aria-label={`Diagram components list, ${nonCutComponents.length} items`}
            >
              {nonCutComponents.map((component, ind) => {
                const isFirst = ind === 0;
                const isDragging = dragState.draggedId === component.id;
                const isDragOver = dragState.dragOverId === component.id;
                const isDropOnTop = isDragOver && dragState.dropPosition === "top";
                const isDropOn = isDragOver && dragState.dropPosition === "on";
                const canDrop = isDragOver && isValidDropTarget(component.id);

                return (
                  <div
                    key={component.id}
                    ref={(el) => {
                      componentRefs.current[component.id] = el;
                    }}
                    className="relative"
                    role="listitem"
                    aria-label={`${component.shape} component, position ${ind + 1} of ${nonCutComponents.length}`}
                  >
                    {/* Drop indicator line - shows above the component */}
                    {isDropOnTop && !isFirst && canDrop && (
                      <div className="absolute -top-1 left-0 right-0 h-1 bg-blue-500 rounded-full z-10" />
                    )}

                    {/* Draggable wrapper */}
                    <div
                      draggable={!isFirst && !component.cut}
                      onDragStart={(e) => !isFirst && handleDragStart(e, component.id)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, component.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, component.id, isFirst)}
                      tabIndex={!isFirst && !component.cut ? 0 : -1}
                      role={!isFirst && !component.cut ? "button" : undefined}
                      aria-roledescription={!isFirst && !component.cut ? "draggable item" : undefined}
                      aria-pressed={isDragging}
                      aria-describedby={!isFirst && !component.cut ? "dnd-instructions" : undefined}
                      className={cn(
                        "relative transition-opacity duration-200",
                        isDragging && "opacity-50",
                        isDropOn && canDrop && "outline outline-2 outline-blue-500 outline-offset-2 rounded-lg",
                        !isFirst && !component.cut && "cursor-grab active:cursor-grabbing focus:outline-2 focus:outline-primary focus:outline-offset-2"
                      )}
                    >
                      {/* Drag handle indicator */}
                      {!isFirst && !component.cut && (
                        <div
                          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
                          aria-hidden="true"
                        >
                          <GripVertical className="w-4 h-4 text-muted" />
                        </div>
                      )}

                      <DiagramComponentCard
                        component={component}
                        index={componentIndexMap[component.id]}
                        parentIndex={getParentIndex(component)}
                        isFirst={isFirst}
                        onScrollToParent={handleScrollToParent}
                      />

                      {/* Drop overlay indicator */}
                      {isDropOn && canDrop && (
                        <div className="absolute inset-0 bg-blue-500/10 rounded-lg pointer-events-none flex items-center justify-center">
                          <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded">
                            Drop to attach here
                          </span>
                        </div>
                      )}

                      {/* Invalid drop indicator */}
                      {isDragOver && !canDrop && dragState.draggedId !== component.id && (
                        <div className="absolute inset-0 bg-red-500/10 rounded-lg pointer-events-none flex items-center justify-center">
                          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">
                            Cannot drop here
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Cut/Copied components section */}
      {cutComponents.length > 0 && (
        <div className="border-t border-surface-border bg-surface">
          <h3 className="text-lg font-semibold px-4 py-3 text-white">
            {diagram.isCopied ? "Copied" : "Cut"} Objects
          </h3>
          <div
            className={cn("overflow-auto max-h-48 px-4 pb-4", CUSTOM_SCROLLBAR)}
          >
            <div className="space-y-2">
              {cutComponents.map((component, ind) => (
                <DiagramComponentCard
                  key={component.id}
                  component={component}
                  index={componentIndexMap[component.id]}
                  parentIndex={getParentIndex(component)}
                  isFirst={ind === 0}
                  onScrollToParent={handleScrollToParent}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Screen reader instructions for drag and drop */}
      <div id="dnd-instructions" className="sr-only">
        Press Space or Enter to start dragging. While dragging, use arrow keys to move and Space or Enter to drop.
      </div>

      {/* Live region for drag and drop status announcements */}
      <div
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {dragState.draggedId && `Dragging component. Drop on a target to reposition.`}
      </div>

      {/* Position Selection Dialog */}
      <Dialog open={!!pendingDrop} onOpenChange={(open) => !open && handleCancelDrop()}>
        <DialogContent className="bg-surface border-surface-border text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Move className="w-5 h-5" />
              Select Attachment Position
            </DialogTitle>
          </DialogHeader>
          {pendingDrop && (
            <div className="space-y-4">
              <p className="text-sm text-muted">
                Move <span className="text-white font-medium">{pendingDrop.draggedName}</span> to{" "}
                <span className="text-primary font-medium">{pendingDrop.targetName}</span>
              </p>
              <p className="text-xs text-muted">Choose where to attach:</p>
              <div className="grid grid-cols-2 gap-2">
                {pendingDrop.availablePositions.map((position) => (
                  <Button
                    key={position}
                    variant="outline"
                    className="text-white border-surface-border hover:bg-surface-hover hover:border-primary"
                    onClick={() => handlePositionSelect(position)}
                  >
                    {position}
                  </Button>
                ))}
              </div>
              <div className="flex justify-end pt-2">
                <Button
                  variant="ghost"
                  className="text-muted hover:text-white"
                  onClick={handleCancelDrop}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompositionTab;
